import CovidDrivingChart from '@root/joinroot.com/src/components/covid-driving/chart-section';
import CovidDrivingExpandables from '@root/joinroot.com/src/components/covid-driving/covid-driving-expandables';
import CovidDrivingGlobalControls from '@root/joinroot.com/src/components/covid-driving/global-controls';
import CovidDrivingMap from '@root/joinroot.com/src/components/covid-driving/map-section';
import React, { useEffect, useMemo, useRef, useState } from '@root/vendor/react';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import { StyleSheet } from '@root/core/src/utils/styles';
import { graphql, useStaticQuery } from 'gatsby';

const METRICS = {
  TRIP_COUNT: 'trip_count',
  TRIP_MILES: 'trip_miles',
  TRIP_MINUTES: 'trip_minutes',
};

const METRICS_TO_UNITS = {
  [METRICS.TRIP_COUNT]: null,
  [METRICS.TRIP_MILES]: ' miles',
  [METRICS.TRIP_MINUTES]: ' minutes',
};

export default function CovidDriving() {
  const data = useStaticQuery(graphql`
    query MyQuery {
      allTripMilesData {
        nodes {
          national
          states
        }
      }
      allTripCountsData {
        nodes {
          national
          states
        }
    }
      allTripMinutesData {
        nodes {
          national
          states
        }
      }
    }
    `);
  const metricsToFileData = useMemo(() => ({
    [METRICS.TRIP_COUNT]: transformDataObject(data.allTripCountsData['nodes'][0], dataIngestionTransformations, METRICS.TRIP_COUNT),
    [METRICS.TRIP_MILES]: transformDataObject(data.allTripMilesData['nodes'][0], dataIngestionTransformations, METRICS.TRIP_MILES),
    [METRICS.TRIP_MINUTES]: transformDataObject(data.allTripMinutesData['nodes'][0], dataIngestionTransformations, METRICS.TRIP_MINUTES),
  }), [data]);
  const earliestDate = useRef(new Date('2020-03-01T00:00:00'));
  const latestDate = useRef(getLatestDate(metricsToFileData));

  const [metric, setMetric] = useState(METRICS.TRIP_COUNT);
  const [startDate, setStartDate] = useState(new Date('2020-03-13T00:00:00'));
  const [endDate, setEndDate] = useState(latestDate.current);
  const [dataToDisplay, setDataToDisplay] = useState(metricsToFileData[metric]);

  const { trackClick } = useAnalytics('COVID_DECREASED_DRIVING');

  useEffect(() => {
    const makeLocaleDate = (yearToUse, origDate) => new Date(yearToUse, origDate.getMonth(), origDate.getDate(), 0, 0, 0);

    const filterDataByDate = (dataArray) => {
      if (!dataArray[0]?.x) { return dataArray; }

      const yearToUse = dataArray[0].x.getFullYear();
      const startDateToUse = makeLocaleDate(yearToUse, startDate);
      const endDateToUse = makeLocaleDate(yearToUse, endDate);

      return dataArray.filter((point) => point.x && point.x >= startDateToUse && point.x <= endDateToUse);
    };
    const newDataToDisplay = transformDataObject(metricsToFileData[metric], filterDataByDate);
    setDataToDisplay(newDataToDisplay);
  }, [metric, metricsToFileData, startDate, endDate]);

  const commonProps = {
    dataToDisplay,
    endDate,
    metric,
    metricUnits: METRICS_TO_UNITS[metric],
    startDate,
  };

  return (
    <div css={styles.covidDrivingSection}>
      <CovidDrivingGlobalControls
        earliestDate={earliestDate.current}
        endDate={endDate}
        latestDate={latestDate.current}
        metric={metric}
        setEndDate={setEndDate}
        setMetric={setMetric}
        setStartDate={setStartDate}
        startDate={startDate}
        trackClick={trackClick}
      />
      <CovidDrivingMap {...commonProps} />
      <CovidDrivingChart
        {...commonProps}
        trackClick={trackClick}
      />
      <CovidDrivingExpandables dataToDisplay={dataToDisplay} />
    </div>
  );
}

function transformDataObject(dataObj, transformDataArrayFn, metric) {
  const transformSegments = (segments) => {
    const newSegments = {};

    const dataKeys = ['2019', '2020'];
    if (segments) {
      Object.keys(segments).forEach((key) => {
        if (dataKeys.includes(key)) {
          const dataArrayToUse = segments[key].by_day || segments[key].byDay;

          newSegments[key] = {};
          newSegments[key].byDay = transformDataArrayFn(dataArrayToUse, metric);
          if (key === '2020') {
            newSegments[key].pctChange = getPctChange(newSegments[key].byDay);
          }
        }
      });
      return newSegments;
    }
  };

  const newFileData = {};

  if (dataObj.national) {
    newFileData.national = transformSegments(dataObj.national);
  }
  if (dataObj.states) {
    newFileData.states = {};

    Object.keys(dataObj.states).forEach((stateId) => {
      newFileData.states[stateId] = transformSegments(dataObj.states[stateId]);

      const metroDataToUse = dataObj.states[stateId].metroAreas;
      if (metroDataToUse) {
        newFileData.states[stateId].metroAreas = Object.keys(metroDataToUse).reduce((acc, current_value) => {
          acc[current_value] = transformSegments(metroDataToUse[current_value]);
          return acc;
        }, {});
      }
    });
  }
  return newFileData;
}

function dataIngestionTransformations(dataArray, metric) {
  const xTransformation = (x) => {
    const xAsDate = new Date(`${x}T00:00:00`);
    return new Date('2020', xAsDate.getMonth(), xAsDate.getDate(), 0, 0, 0);
  };

  const yTransformationMetricSpecific = metric === METRICS.TRIP_MINUTES
    ? (y) => {
      const absY = y < 0 ? y * -1 : y;
      return absY / 60;
    }
    : (y) => y;

  const yTransformation = (y) => {
    const yAsNumber = typeof y === 'number' ? y : new Number(y);
    return yTransformationMetricSpecific(yAsNumber);
  };

  return dataArray.map((point) => {
    if (!point) { return point; }

    return {
      x: xTransformation(point.x),
      y: yTransformation(point.y),
    };
  });
}

function getLatestDate(metricsToFileData) {
  const dateArray = metricsToFileData[METRICS.TRIP_COUNT]?.national['2020'].byDay;

  const latestDate = dateArray && dateArray.length > 0 && dateArray[dateArray.length - 1].x
    || new Date('2020-04-19T00:00:00');

  return latestDate;
}

function getPctChange(dataArray) {
  if (!dataArray || dataArray.length === 0) { return 'Unknown'; }

  const v1 = dataArray[0].y;
  const v2 = dataArray[dataArray.length - 1].y;
  const pctChange = (v2 - v1) / v1 * 100;
  return Number.parseFloat(pctChange.toFixed(2));
}

export { METRICS };

const styles = StyleSheet.create({
  covidDrivingSection: {
    position: 'relative', // required for the sticky controls
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 80,
  },
});
